import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { Input } from '@shared/components/ui/input';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FiltersSectionProps {
  onCodeFilterChange: (value: string) => void;
  onActiveFilterChange: (value: boolean | undefined) => void;
  onClearFilters: () => void;
  filterCode: string;
  filterActive: boolean | undefined;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  hasActiveFilters: boolean;
}

export const FiltersSection = ({
  onCodeFilterChange,
  onActiveFilterChange,
  onClearFilters,
  filterCode,
  filterActive,
  columns,
  selectedColumns,
  onColumnsChange,
  hasActiveFilters,
}: FiltersSectionProps) => {
  const { t } = useTranslation('packingStationsModule');
  const [localCode, setLocalCode] = useState(filterCode);

  useEffect(() => {
    setLocalCode(filterCode);
  }, [filterCode]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterCode.trim()) count++;
    if (filterActive !== undefined) count++;
    return count;
  }, [filterCode, filterActive]);

  const statusOptions = [
    { label: t('filters.active'), value: 'true' },
    { label: t('filters.inactive'), value: 'false' },
  ];

  const applyCodeFilter = () => {
    if (localCode !== filterCode) {
      onCodeFilterChange(localCode);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Dropdown de filtros */}
      <FiltersDropdown
        title={t('filters.title')}
        activeFiltersCount={activeFilterCount}
        triggerTestId="packing-stations-filters-toggle"
      >
        <div className="grid grid-cols-2 gap-4 p-1">
          {/* Filtro por código */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{t('filters.code')}</label>
            <Input
              type="text"
              value={localCode}
              onChange={(e) => setLocalCode(e.target.value)}
              onBlur={applyCodeFilter}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyCodeFilter();
              }}
              placeholder={t('filters.codePlaceholder')}
              data-testid="filter-code-input"
            />
          </div>

          {/* Filtro por estado */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{t('filters.status')}</label>
            <Combobox
              options={statusOptions}
              value={filterActive === undefined ? '' : String(filterActive)}
              setValue={(val) => onActiveFilterChange(val === '' ? undefined : val === 'true')}
              placeholder={t('filters.allStatuses')}
              dataTestId="filter-status-select"
            />
          </div>

          {/* Limpiar filtros */}
          {hasActiveFilters && (
            <div className="col-span-2 flex justify-end pt-2 border-t border-card-border">
              <button
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={onClearFilters}
                type="button"
                data-testid="clear-filters-button"
              >
                {t('filters.clear')}
              </button>
            </div>
          )}
        </div>
      </FiltersDropdown>

      {/* Selector de columnas */}
      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={['code']}
        storageKey="packing-stations-table"
      />
    </div>
  );
};
